import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { TransferMoneyCloseConfirmDialog } from "src/features/transfer-money-close-confirm";
import { TransferMoneyFormValues } from "../model/types";
import { useTransferMoneyForm } from "../model/useTransferMoneyForm";
import { TransferAccountsSection } from "./TransferAccountsSection";

type TransferMoneyFormProps = {
  clientId: string;
  onCancel?: () => void;
  onSuccess?: (values: TransferMoneyFormValues) => void;
  onResult?: (result: {
    status: "success" | "partial_success" | "error";
    message: string;
    transfersCount?: number;
    successCount?: number;
    failedCount?: number;
  }) => void;
};

export const TransferMoneyForm = ({
  clientId,
  onCancel,
  onSuccess,
  onResult,
}: TransferMoneyFormProps) => {
  const {
    form: { control },
    fields,
    append,
    remove,

    serviceFromOptions,
    accountFromOptions,
    serviceToOptions,
    accountToOptions,

    servicesFrom,
    accountFromSelect,
    balanceFrom,
    serviceTo,

    accountToBalances,
    accountToBalanceLoading,

    errors,
    isSubmitDisabled,

    isCloseConfirmOpen,
    isSubmitSuccessOpen,

    isInitialLoading,
    isInitialError,
    isAccountDataLoading,
    isAccountDataError,
    refetchInitialData,

    handleSubmit,
    handleServiceFromChange,
    handleAccountFromChange,
    handleServiceToChange,
    handleCancelClick,
    handleCloseConfirm,
    handleConfirmClose,
    handleSuccessSnackbarClose,
    submit,
    previewData,
    previewErrorMessage,
    isPreviewLoading,
    submitErrorMessage,
    submitResultMessage,
    isSubmittingTransfer,
  } = useTransferMoneyForm({
    clientId,
    onCancel,
    onSuccess,
    onResult,
  });

  if (isInitialLoading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isInitialError) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          Не удалось загрузить аккаунты для перевода.
        </Alert>
        <Box>
          <Button variant="outlined" onClick={() => refetchInitialData()}>
            Повторить
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(submit)}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Выберите рекламный сервис для списания
            </Typography>

            <Controller
              name="servicesFrom"
              control={control}
              rules={{ required: "Выберите сервис" }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  value={field.value}
                  onChange={(_, option) => handleServiceFromChange(option)}
                  options={serviceFromOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Сервис для списания"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Выберите аккаунт для списания
            </Typography>

            <Controller
              name="accountFromSelect"
              control={control}
              rules={{ required: "Выберите аккаунт" }}
              render={({ fieldState }) => (
                <Autocomplete
                  value={accountFromSelect}
                  onChange={(_, option) => void handleAccountFromChange(option)}
                  options={accountFromOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionDisabled={(option) => !!option.isDisabled}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  disabled={!servicesFrom}
                  loading={isAccountDataLoading}
                  renderOption={(props, option) => {
                    // eslint-disable-next-line react/prop-types
                    const { key, ...optionProps } = props;

                    return (
                      <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{
                          opacity: option.isDisabled ? 0.65 : 1,
                        }}
                      >
                        {option.label}
                        {option.isDisabled && option.disabledReason ? (
                          <Box
                            component="span"
                            sx={{ color: "warning.main", ml: 0.5 }}
                          >
                            {" "}
                            ({option.disabledReason})
                          </Box>
                        ) : null}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Аккаунт для списания"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>

          {isAccountDataError && (
            <Alert severity="error">
              Не удалось загрузить баланс или доступные аккаунты назначения.
            </Alert>
          )}

          {accountFromSelect && balanceFrom != null && (
            <Stack spacing={1}>
              <Typography variant="body1">
                Текущий баланс: <b>{balanceFrom}</b>{" "}
                {accountFromSelect.currency}
              </Typography>

              {previewData?.accountFrom?.newBalance != null && (
                <Typography
                  variant="body1"
                  color={
                    previewData.accountFrom.newBalance < 0
                      ? "error.main"
                      : "text.primary"
                  }
                >
                  Итоговый баланс: <b>{previewData.accountFrom.newBalance}</b>{" "}
                  {accountFromSelect.currency}
                </Typography>
              )}
            </Stack>
          )}

          {!!serviceToOptions.length && (
            <>
              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Выберите рекламный сервис для зачисления
                </Typography>

                <Controller
                  name="serviceTo"
                  control={control}
                  rules={{ required: "Выберите сервис назначения" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      value={field.value}
                      onChange={(_, option) => handleServiceToChange(option)}
                      options={serviceToOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      disabled={!accountFromSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Сервис для зачисления"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </>
          )}

          {!!serviceTo && !!fields.length && (
            <>
              <Divider />

              <TransferAccountsSection
                control={control}
                errors={errors}
                fields={fields}
                append={append}
                remove={remove}
                accountToOptions={accountToOptions}
                accountToBalances={accountToBalances}
                accountToBalanceLoading={accountToBalanceLoading}
              />
            </>
          )}

          {previewData?.accountFrom?.newBalance != null &&
            previewData.accountFrom.newBalance < 0 && (
              <Alert severity="error">
                Сумма перевода превышает остаток на аккаунте.
              </Alert>
            )}

          {!!serviceTo &&
            !!fields.length &&
            (isPreviewLoading ? (
              <Alert severity="info">Пересчитываем превью перевода...</Alert>
            ) : (
              <>
                {!!previewErrorMessage && (
                  <Alert severity="error">{previewErrorMessage}</Alert>
                )}

                {previewData && (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      К списанию: <b>{previewData.accountFrom.toWithdraw}</b>{" "}
                      {previewData.accountFrom.burden?.currency ??
                        accountFromSelect?.currency}
                    </Typography>

                    {previewData.accountFrom.burden && (
                      <Typography variant="body2">
                        Комиссия: <b>{previewData.accountFrom.burden.amount}</b>{" "}
                        {previewData.accountFrom.burden.currency} (
                        {previewData.accountFrom.burden.feeQuantity}
                        {previewData.accountFrom.burden.feeUnit})
                      </Typography>
                    )}
                  </Stack>
                )}
              </>
            ))}

          {!!submitResultMessage && (
            <Alert severity="success">{submitResultMessage}</Alert>
          )}

          {!!submitErrorMessage && (
            <Alert severity="error">{submitErrorMessage}</Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              type="button"
              onClick={handleCancelClick}
            >
              Отменить
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitDisabled}
            >
              {isSubmittingTransfer ? "Переводим..." : "Перевести средства"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TransferMoneyCloseConfirmDialog
        open={isCloseConfirmOpen}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmClose}
      />

      <Snackbar
        open={isSubmitSuccessOpen}
        autoHideDuration={3000}
        onClose={handleSuccessSnackbarClose}
        message="Операция успешно принята к исполнению, результат вы можете найти на странице 'История переводов'"
      />
    </>
  );
};
